import React, { useMemo, useState } from "react";
import { isEmpty, isNull, map } from "lodash";
import {
  DealData,
  DealQuestionnaireQuestionSettings,
  DealsQuestionType,
  QuestionResponse,
  Responses,
  Score
} from "@/pages/deals/types";
import {
  useDealQuestionnaireQuestionOptions,
  useUpdateDealQuestionnaireMutation,
  useCreateQuestionnaireOptionMutation
} from "@/pages/deals/hooks";
import BaseQuestion from "./BaseQuestion";
import { MultiSelect } from "@/components/controls/MultiSelect";
import { useTranslation } from "react-i18next";
import CreateNewOptionModal from "@/pages/settings/components/userManagement/CreateNewOptionModal";
import { AddButtonFooter } from "@/components/ui/add-button-footer";

interface Props {
  question: DealQuestionnaireQuestionSettings;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  questionnaireId: string;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  onResponseError: () => void;
  dealData?: DealData;
  className?: string;
}

const MultiSelectQuestion: React.FC<Props> = ({
  question,
  localResponses,
  updateResponse,
  questionnaireId,
  handleUpdateQuestionnaireScore,
  onResponseError,
  dealData,
  className
}) => {
  const { t } = useTranslation();
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();
  const createQuestionnaireOption = useCreateQuestionnaireOptionMutation();
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  if (!dealData) {
    return null;
  }

  const { data: optionsData, refetch: refetchOptions } =
    useDealQuestionnaireQuestionOptions(
      question.id,
      question.responseType?.type === DealsQuestionType.OPTIONS
    );

  const options = useMemo(
    () => map(optionsData, (op) => ({ value: op.id, label: op.option })),
    [optionsData]
  );
  const allowUsersToAdd = question.responseType?.allowUsersToAdd ?? false;
  const answer = localResponses[question.id]?.response?.value;
  const arrayOrNullAnswer: Array<string> | null =
    typeof answer === "object" || answer === null ? answer : null;

  const handleSelect = (newValues: string[]) => {
    const currentValues = Array.isArray(arrayOrNullAnswer)
      ? arrayOrNullAnswer
      : [];
    const sortedNew = [...newValues].sort();
    const sortedCurrent = [...currentValues].sort();
    if (
      sortedNew.length === sortedCurrent.length &&
      sortedNew.every((value, index) => value === sortedCurrent[index])
    ) {
      return;
    }

    const existingResponse =
      localResponses[question.id] || ({} as QuestionResponse | null);
    const newResponse = {
      ...existingResponse,
      response: { value: newValues },
      lastEditedBy: undefined
    };

    updateResponse(question.id, newResponse);

    updateQuestionnaire.mutate(
      {
        pathParams: { dealId: dealData.id, questionnaireId },
        data: {
          questions: [
            {
              questionId: question.id,
              ...newResponse
            }
          ]
        }
      },
      {
        onSuccess: (data) => {
          handleUpdateQuestionnaireScore(data.score);
        },
        onError: onResponseError
      }
    );
  };

  const handleCreateNewOption = async (optionName: string) => {
    try {
      const response = await createQuestionnaireOption.mutateAsync({
        pathParams: {
          questionId: question.id
        },
        data: {
          options: [optionName]
        }
      });
      await refetchOptions();

      // Automatically select the newly created option
      const currentValues = arrayOrNullAnswer || [];
      const newOptionId = response.successfulIds[0];
      if (newOptionId) {
        const newValues = [...currentValues, newOptionId];
        handleSelect(newValues);
      }

      setSearchTerm("");
    } catch (error) {
      console.error("Failed to create questionnaire option:", error);
    }
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  return (
    <>
      <BaseQuestion
        question={question}
        questionnaireId={questionnaireId}
        dealId={dealData.id}
        handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
        localResponses={localResponses}
        updateResponse={updateResponse}
        onResponseError={onResponseError}
        questionContainerClassName={className}
      >
        <MultiSelect
          options={filteredOptions}
          selectedValues={
            isNull(arrayOrNullAnswer) ? undefined : arrayOrNullAnswer
          }
          onValueChange={handleSelect}
          showSearch
          onSearch={handleSearch}
          searchPlaceholder={
            allowUsersToAdd
              ? t(
                  `multiSelect.${isEmpty(options) ? "typeToCreate" : "searchOrCreate"}`
                )
              : t("search")
          }
          placeholder={t("select")}
          maxCount={4}
          matchTriggerWidth={true}
          formatter={(value: string) => {
            const option = options.find((opt) => opt.value === value);
            return option ? option.label : value;
          }}
          popOverFooter={
            allowUsersToAdd &&
            (isEmpty(options) ||
              (searchTerm.trim() && filteredOptions.length === 0)) ? (
              <AddButtonFooter
                buttonText={t(
                  `multiSelect.${searchTerm.trim() ? "createNewOptionFor" : "createNewOption"}`,
                  {
                    optionName: `"${searchTerm}"`
                  }
                )}
                disabled={!searchTerm.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setNewOptionName(searchTerm);
                  setShowCreateOptionModal(true);
                }}
              />
            ) : undefined
          }
          className={className}
        />
      </BaseQuestion>
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        optionName={newOptionName}
      />
    </>
  );
};

export default MultiSelectQuestion;
